import { DiagramComponent } from '../../core/DiagramComponent';
import { Slider } from '../interactive/Slider';
import type { TenFrameConfig, Variables, JXGBoard, JXGElement } from '../../types/interfaces';

interface ColorMapping {
  color: string;
  count: number;
}

interface SliderConfig {
  enabled?: boolean;
  position?: 'below' | 'above' | 'right' | 'left';
  offset?: number;
  min?: number;
  max?: number;
  variable?: string;
  label?: string;
  color?: string;
  showTicks?: boolean;
  showTickLabels?: boolean;
  tickStep?: number;
}

interface ExtendedTenFrameConfig extends TenFrameConfig {
  mode?: 'five' | 'ten';
  maxCapacity?: number;
  cellSpacing?: number;
  strokeColor?: string;
  strokeWidth?: number;
  fillColor?: string;
  colorMap?: Array<{ color: string; count: number | string }>;
  label?: string;
  labelSize?: number;
  labelColor?: string;
  slider?: SliderConfig;
}

/**
 * Ten Frame component for representing numbers 0-30
 * Displays up to 3 rows of 10 cells each
 * Optionally includes an integrated slider for interaction
 */
export class TenFrame extends DiagramComponent<ExtendedTenFrameConfig> {
  private _updateTimeout: ReturnType<typeof setTimeout> | null = null;
  private _lastValue: number | null = null;
  private _slider: Slider | null = null;

  constructor(config: ExtendedTenFrameConfig, board: JXGBoard, parentState: Variables = {}) {
    super(config, board, parentState);
  }

  render(): void {
    const config = this.config;
    
    // Evaluate value expression
    const value = Math.round(Number(this.evaluateExpression(config.value || 0)));
    
    // Mode: 'ten' (10 cells per row) or 'five' (5 cells per row, default)
    const mode = config.mode || 'five';
    const cellsPerRow = mode === 'five' ? 5 : 10;
    
    // Maximum capacity (default 15 for 'five' mode, 30 for 'ten' mode)
    const maxCapacity = config.maxCapacity || (mode === 'five' ? 15 : 30);
    
    // Validate range
    if (value < 0 || value > maxCapacity) {
      console.error(`TenFrame value ${value} out of range [0-${maxCapacity}]`);
      return;
    }
    
    // Position and size configuration
    const x = Number(this.evaluateExpression(config.position?.[0] || 0));
    const y = Number(this.evaluateExpression(config.position?.[1] || 0));
    const cellSize = config.cellSize || 0.8;
    const cellSpacing = config.cellSpacing || 0.1;
    const strokeColor = config.strokeColor || '#000';
    const strokeWidth = config.strokeWidth || 2;
    const fillColor = config.fillColor || '#4CAF50';
    const emptyColor = config.emptyColor || '#fff';
    
    // Evaluate colorMap if provided
    let evaluatedColorMap: ColorMapping[] | null = null;
    if (config.colorMap && Array.isArray(config.colorMap)) {
      evaluatedColorMap = config.colorMap.map((mapping: { color: string; count: number | string }) => ({
        color: mapping.color,
        count: Math.round(Number(this.evaluateExpression(mapping.count)))
      }));
    }
    
    // Calculate number of rows to render
    const maxRows = Math.ceil(maxCapacity / cellsPerRow);
    // In 'five' mode, always show at least 2 rows (10 cells)
    const minRows = mode === 'five' ? 2 : 1;
    const rowsToRender = Math.max(minRows, Math.min(maxRows, Math.ceil(value / cellsPerRow)));
    
    for (let row = 0; row < rowsToRender; row++) {
      for (let col = 0; col < cellsPerRow; col++) {
        const cellIndex = row * cellsPerRow + col;
        const cellX = x + col * (cellSize + cellSpacing);
        const cellY = y - row * (cellSize + cellSpacing);
        
        // Determine if this cell is filled
        const isFilled = cellIndex < value;
        
        // Determine fill color
        let cellFillColor = emptyColor;
        if (isFilled) {
          if (evaluatedColorMap) {
            // Use color map: [{color: '#color', count: n}, ...]
            let accumulated = 0;
            for (const mapping of evaluatedColorMap) {
              accumulated += mapping.count;
              if (cellIndex < accumulated) {
                cellFillColor = mapping.color;
                break;
              }
            }
          } else {
            cellFillColor = fillColor;
          }
        }
        
        // Create fixed rectangle using 4 corner points
        const p1 = this.board.create('point', [cellX, cellY], {
          visible: false,
          fixed: true,
          withLabel: false,
          name: ''
        });
        const p2 = this.board.create('point', [cellX + cellSize, cellY], {
          visible: false,
          fixed: true,
          withLabel: false,
          name: ''
        });
        const p3 = this.board.create('point', [cellX + cellSize, cellY - cellSize], {
          visible: false,
          fixed: true,
          withLabel: false,
          name: ''
        });
        const p4 = this.board.create('point', [cellX, cellY - cellSize], {
          visible: false,
          fixed: true,
          withLabel: false,
          name: ''
        });
        
        const rect = this.board.create('polygon', [p1, p2, p3, p4], {
          fillColor: cellFillColor,
          fillOpacity: 1,
          strokeColor: strokeColor,
          strokeWidth: strokeWidth,
          borders: { fixed: true },
          vertices: { fixed: true },
          fixed: true,
          highlight: false,
          hasInnerPoints: false,
          layer: 5,
          cssClass: 'jxg-grid-line'
        });
        
        this.jsxElements.push(p1, p2, p3, p4, rect);
      }
    }
    
    // Add label if provided
    if (config.label) {
      const labelText = this.evaluateExpression(config.label);
      const labelX = x + (cellsPerRow / 2) * (cellSize + cellSpacing) - cellSpacing / 2;
      const labelY = y + 0.5;
      
      const text = this.board.create('text', [labelX, labelY, labelText], {
        fontSize: config.labelSize || 16,
        color: config.labelColor || '#000',
        anchorX: 'middle',
        anchorY: 'middle',
        fixed: true,
        highlight: false,
        layer: 8,
        cssClass: 'jxg-text-no-pointer'
      });
      
      this.jsxElements.push(text);
    }
    
    // Add integrated slider if configured
    if (config.slider?.enabled) {
      this._renderSlider(x, y, cellSize, cellSpacing, cellsPerRow, rowsToRender);
    }
  }
  
  /**
   * Render integrated slider for the ten frame
   */
  private _renderSlider(x: number, y: number, cellSize: number, cellSpacing: number, cellsPerRow: number, rowsToRender: number): void {
    const sliderConfig = this.config.slider!;
    const mode = this.config.mode || 'five';
    const maxCapacity = this.config.maxCapacity || (mode === 'five' ? 15 : 30);
    
    // Calculate slider position based on configuration
    const position = sliderConfig.position || 'below';
    const offset = sliderConfig.offset || 1.5;
    
    let sliderX1: number, sliderY: number, sliderX2: number;
    const frameWidth = cellsPerRow * (cellSize + cellSpacing) - cellSpacing;
    
    switch (position) {
      case 'below':
        sliderX1 = x;
        sliderY = y - rowsToRender * (cellSize + cellSpacing) - offset;
        sliderX2 = x + frameWidth;
        break;
      case 'above':
        sliderX1 = x;
        sliderY = y + offset;
        sliderX2 = x + frameWidth;
        break;
      case 'right':
        sliderX1 = x + frameWidth + offset;
        sliderY = y - (rowsToRender * (cellSize + cellSpacing)) / 2;
        sliderX2 = sliderX1 + 5;
        break;
      case 'left':
        sliderX1 = x - offset - 5;
        sliderY = y - (rowsToRender * (cellSize + cellSpacing)) / 2;
        sliderX2 = x - offset;
        break;
      default:
        sliderX1 = x;
        sliderY = y - rowsToRender * (cellSize + cellSpacing) - offset;
        sliderX2 = x + frameWidth;
    }
    
    // Create slider component
    const sliderComponentConfig = {
      type: 'slider' as const,
      position: [sliderX1, sliderY, sliderX2] as [number, number, number],
      min: sliderConfig.min ?? 0,
      max: sliderConfig.max ?? maxCapacity,
      value: this.config.value,
      variable: sliderConfig.variable || 'n',
      label: sliderConfig.label || '',
      mode: 'integer' as const,
      color: sliderConfig.color || this.config.fillColor || '#4CAF50',
      gliderColor: sliderConfig.color || this.config.fillColor || '#4CAF50',
      showTicks: sliderConfig.showTicks ?? true,
      showTickLabels: sliderConfig.showTickLabels ?? true,
      tickStep: sliderConfig.tickStep ?? 1
    };
    
    this._slider = new Slider(sliderComponentConfig, this.board, this.getState());
    
    // Setup event propagation from slider
    this._slider.onEvent = (event) => {
      this._emitEvent(event.type, event.detail);
    };
    
    const sliderElement = this._slider.render();
    if (sliderElement) {
      this.jsxElements.push(sliderElement);
    }
  }

  /**
   * Handle updates when variables change
   */
  update(newConfig?: Partial<ExtendedTenFrameConfig>): void {
    // Update parent state for expression evaluation
    if (newConfig) {
      this.config = { ...this.config, ...newConfig };
    }
    this._rerender();
  }

  /**
   * Update ten frame with debouncing for smooth updates
   */
  protected _rerender(): void {
    const config = this.config;
    const newValue = Math.round(Number(this.evaluateExpression(config.value || 0)));
    
    // Skip if value hasn't changed
    if (newValue === this._lastValue) {
      return;
    }
    
    // Clear any pending update
    if (this._updateTimeout) {
      clearTimeout(this._updateTimeout);
    }
    
    // Debounce updates to avoid jerkiness during rapid changes
    this._updateTimeout = setTimeout(() => {
      this._lastValue = newValue;
      
      // Don't destroy slider during updates
      if (this._slider) {
        // Update slider's parent state
        this._slider.parentState = this.parentState;
      }
      
      // Destroy and recreate (slider will be preserved if it exists)
      this.destroy();
      this.render();
      this._updateTimeout = null;
    }, 50); // 50ms debounce
  }
  
  /**
   * Override destroy to clean up slider
   */
  destroy(): void {
    // Don't destroy slider - it's interactive
    if (this._slider) {
      // Remove slider from jsxElements before destroying
      const sliderElements = this._slider.jsxElements;
      this.jsxElements = this.jsxElements.filter(el => !sliderElements.includes(el));
    }
    
    super.destroy();
  }

  getSpecificity(): number {
    return 20; // Background element
  }
}
